import { SharedModule } from '@/app/shared/shared.module';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize } from 'rxjs/operators';
import {
  EnterpriseProxy,
  UserEnterpriseInfoViewModel,
  CsiContractProxy,
  ImpactDashboardProxy,
  LookupProxy,
} from '@/app/proxy/proxies';
import { SustainabilityDashboardService } from './services/sustainability-dashboard.service';
import { SustainabilityDashboardAnalyticsService } from './services/sustainability-dashboard-analytics.service';
import { SustainabilityDashboardEmptyContractsComponent } from './components/sustainability-dashboard-empty-contracts/sustainability-dashboard-empty-contracts.component';

export enum SustainabilityTab {
  Home = 'home_',
  Water = 'water',
  Soil = 'soil',
  Climate = 'climate',
  Biodiversity = 'biodiversity',
  Livelihood = 'livelihood',
}

export const SUSTAINABILITY_TABS: { value: SustainabilityTab; label: string }[] = [
  { value: SustainabilityTab.Home, label: 'Home' },
  { value: SustainabilityTab.Water, label: 'Water' },
  { value: SustainabilityTab.Soil, label: 'Soil' },
  { value: SustainabilityTab.Climate, label: 'Climate' },
  { value: SustainabilityTab.Biodiversity, label: 'Biodiversity' },
  { value: SustainabilityTab.Livelihood, label: 'Livelihood' },
];

interface StoredEnterpriseFilters {
  contractId?: string;
  subcontractId?: string | null;
}

@Component({
  selector: 'app-sustainability-dashboard',
  imports: [SharedModule, SustainabilityDashboardEmptyContractsComponent],
  templateUrl: './sustainability-dashboard.component.html',
  styleUrl: './sustainability-dashboard.component.less',
  providers: [EnterpriseProxy, CsiContractProxy, ImpactDashboardProxy, LookupProxy],
})
export class SustainabilityDashboardComponent implements OnInit {
  readonly SustainabilityTab = SustainabilityTab;

  readonly selectedTab = signal(SustainabilityTab.Home);
  readonly userEnterprises = signal<UserEnterpriseInfoViewModel[]>([]);
  readonly selectedEnterprise = signal<UserEnterpriseInfoViewModel | null>(null);
  readonly contractOptions = signal<{ contractName: string; contractId: string }[]>([]);
  readonly selectedContractId = signal<string | null>(null);
  readonly subcontractOptions = signal<{ key: string; value: string }[]>([]);
  readonly selectedSubcontractId = signal<string | null>(null);
  readonly enterprisesLoading = signal(true);
  readonly loading = signal(false);
  readonly contractsLoadError = signal(false);

  readonly hasContracts = computed(() => this.contractOptions().length > 0);
  readonly showContractsEmptyState = computed(
    () => !!this.selectedEnterprise() && !this.loading() && !this.hasContracts()
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly DASHBOARD_BASE_ROUTE = '/sustainability-dashboard-d';
  private readonly ENTERPRISE_STORAGE_KEY = 'sustainability_selected_enterprise_id';
  private readonly FILTER_STORAGE_KEY = 'sustainability_selected_filters';
  private readonly _router = inject(Router);
  private readonly _enterpriseProxy = inject(EnterpriseProxy);
  private readonly _csiContractProxy = inject(CsiContractProxy);
  private readonly _lookupProxy = inject(LookupProxy);
  private readonly _sustainabilityService = inject(SustainabilityDashboardService);
  private readonly _analyticsService = inject(SustainabilityDashboardAnalyticsService);

  tabOptions: { value: SustainabilityTab; label: string; show: boolean }[] = [
    { value: SustainabilityTab.Home, label: 'Home', show: true },
    { value: SustainabilityTab.Water, label: 'Water', show: true },
    { value: SustainabilityTab.Soil, label: 'Soil', show: true },
    { value: SustainabilityTab.Climate, label: 'Climate', show: true },
    { value: SustainabilityTab.Biodiversity, label: 'Biodiversity', show: true },
    { value: SustainabilityTab.Livelihood, label: 'Livelihood', show: true },
  ];

  ngOnInit(): void {
    this.syncSelectedTabFromRoute();
    // Keep the active tab in sync when navigating between dashboard routes.
    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncSelectedTabFromRoute());

    this.loadUserEnterprises();
  }

  // Loads enterprises, then restores the last selected one from localStorage.
  private loadUserEnterprises(): void {
    this.enterprisesLoading.set(true);
    this._enterpriseProxy
      .enterprise_GetUserEnterprises()
      .pipe(finalize(() => this.enterprisesLoading.set(false)))
      .subscribe((res) => {
        if (res.isSuccess && res.result?.length > 0) {
          this.userEnterprises.set(res.result);

          const storedId = localStorage.getItem(this.ENTERPRISE_STORAGE_KEY);
          const stored = storedId ? res.result.find((e) => e.enterpriseId === storedId) : null;
          this.onSelectEnterpriseChange(stored ?? res.result[0]);
        }
      });
  }

  // Persists enterprise choice and reloads contracts for the selected enterprise.
  onSelectEnterpriseChange(enterprise: UserEnterpriseInfoViewModel): void {
    this.selectedEnterprise.set(enterprise);
    localStorage.setItem(this.ENTERPRISE_STORAGE_KEY, enterprise.enterpriseId);
    this._sustainabilityService.selectEnterprise(enterprise);
    this.getContracts(enterprise.enterpriseId);
  }

  // Loads contracts for an enterprise and restores the last saved contract/subcontract filters.
  getContracts(enterpriseId: string): void {
    this.contractOptions.set([]);
    this.selectedContractId.set(null);
    this.subcontractOptions.set([]);
    this.selectedSubcontractId.set(null);
    this.contractsLoadError.set(false);
    this._sustainabilityService.selectContract(null, null);
    this._sustainabilityService.selectSubcontract(null);

    this.loading.set(true);
    this._csiContractProxy
      .csiContract_GetCsiContracts(enterpriseId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (!res?.isSuccess) {
            this.contractsLoadError.set(true);
            return;
          }
          if (res.result?.length > 0) {
            const options = res.result.map((contract) => ({
              contractId: contract.id,
              contractName: contract.name || 'Unnamed Contract',
            }));
            this.contractOptions.set(options);

            // Restore saved contract when still available; otherwise use the first one.
            const stored = this.readStoredFilters(enterpriseId);
            const selectedContract =
              options.find((contract) => contract.contractId === stored.contractId) ?? options[0];
            // Only restore subcontract when it belongs to the restored contract.
            const preferredSubcontractId =
              stored.contractId === selectedContract.contractId ? stored.subcontractId : null;

            this.selectedContractId.set(selectedContract.contractId);
            this._sustainabilityService.selectContract(
              selectedContract.contractId,
              selectedContract.contractName
            );
            this.getSubcontracts(selectedContract.contractId, preferredSubcontractId);
          }
        },
        error: () => {
          this.contractsLoadError.set(true);
        },
      });
  }

  onSelectContractId(contractId: string | null): void {
    this.selectedContractId.set(contractId);
    this.onSelectContract();
  }

  onSelectContract(): void {
    const contractId = this.selectedContractId();
    const selected = this.contractOptions().find((c) => c.contractId === contractId);
    this._sustainabilityService.selectContract(contractId, selected?.contractName ?? null);

    const enterpriseId = this.selectedEnterprise()?.enterpriseId;
    if (enterpriseId && contractId) {
      // Changing contract clears the saved subcontract for this enterprise.
      this.writeStoredContract(enterpriseId, contractId);
    }

    if (contractId) {
      this.getSubcontracts(contractId);
    }
  }

  // Loads subcontracts for a contract; optionally restores a previously saved subcontract.
  getSubcontracts(contractId: string, preferredSubcontractId: string | null = null): void {
    this.subcontractOptions.set([]);
    this.selectedSubcontractId.set(null);
    this._sustainabilityService.selectSubcontract(null);

    this._lookupProxy.lookup_GetSubcontractsByContract(contractId).subscribe({
      next: (res) => {
        if (res?.isSuccess && res.result?.length > 0) {
          const options = res.result.map((sc) => ({
            key: sc.key,
            value: sc.value ?? sc.key,
          }));
          this.subcontractOptions.set(options);

          // Restore saved subcontract only when it still exists for this contract.
          const selectedSubcontract = preferredSubcontractId
            ? options.find((sc) => sc.key === preferredSubcontractId) ?? null
            : null;
          if (selectedSubcontract) {
            this.selectedSubcontractId.set(selectedSubcontract.key);
            this._sustainabilityService.selectSubcontract(
              selectedSubcontract.key,
              selectedSubcontract.value
            );
          }
        }

        this._analyticsService.trackTabViewed(this.selectedTab());
      },
      error: () => {
        // Handle error silently or log
      },
    });
  }

  onSelectSubcontractId(subcontractId: string | null): void {
    this.selectedSubcontractId.set(subcontractId);
    this.onSelectSubcontract();
  }

  onSelectSubcontract(): void {
    const subcontractId = this.selectedSubcontractId();
    const selected = this.subcontractOptions().find((sc) => sc.key === subcontractId);
    this._sustainabilityService.selectSubcontract(subcontractId, selected?.value ?? null);
    this._analyticsService.trackTabViewed(this.selectedTab());

    const enterpriseId = this.selectedEnterprise()?.enterpriseId;
    if (enterpriseId) {
      this.writeStoredSubcontract(enterpriseId, subcontractId);
    }
  }

  // Reloads contracts for the current enterprise; saved filters are restored in getContracts.
  onRefresh(): void {
    const enterprise = this.selectedEnterprise();
    if (enterprise?.enterpriseId) {
      this._analyticsService.resetViewTracking();
      this.getContracts(enterprise.enterpriseId);
    }
  }

  onSelectTab(tab: SustainabilityTab): void {
    this.selectedTab.set(tab);
    this._router.navigate([this.DASHBOARD_BASE_ROUTE, tab]);
  }

  onSelectEnterpriseFromModel(enterprise: UserEnterpriseInfoViewModel | null): void {
    if (enterprise) {
      this.onSelectEnterpriseChange(enterprise);
    }
  }

  private syncSelectedTabFromRoute(): void {
    const url = this._router.url;
    const tabMatch = url.match(/\/sustainability-dashboard-d\/(\w+)/);
    const path = tabMatch?.[1];
    if (path && Object.values(SustainabilityTab).includes(path as SustainabilityTab)) {
      this.selectedTab.set(path as SustainabilityTab);
      this._analyticsService.trackTabViewed(path as SustainabilityTab);
    }
  }

  // Contract/subcontract filters are stored per enterprise so page refresh keeps the same scope.
  private readAllStoredFilters(): Record<string, StoredEnterpriseFilters> {
    try {
      const raw = localStorage.getItem(this.FILTER_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      return JSON.parse(raw) as Record<string, StoredEnterpriseFilters>;
    } catch {
      return {};
    }
  }

  private readStoredFilters(enterpriseId: string): {
    contractId: string | null;
    subcontractId: string | null;
  } {
    const stored = this.readAllStoredFilters()[enterpriseId];
    return {
      contractId: stored?.contractId ?? null,
      subcontractId: stored?.subcontractId ?? null,
    };
  }

  // Saves contract selection and resets subcontract because subcontracts belong to one contract.
  private writeStoredContract(enterpriseId: string, contractId: string): void {
    const all = this.readAllStoredFilters();
    all[enterpriseId] = { contractId, subcontractId: null };
    localStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify(all));
  }

  // Saves subcontract selection; null means the user cleared the filter.
  private writeStoredSubcontract(enterpriseId: string, subcontractId: string | null): void {
    const all = this.readAllStoredFilters();
    const contractId = all[enterpriseId]?.contractId ?? this.selectedContractId();
    if (!contractId) {
      return;
    }

    all[enterpriseId] = { contractId, subcontractId };
    localStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify(all));
  }
}
